import React, { useMemo } from 'react';
import I18n from 'i18n-js';
import { useQueryClient } from '@tanstack/react-query';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import * as Routes from 'modules/routes';
import AsyncAssignmentModal from 'components/staffer/AssignmentModal/AsyncAssignmentModal';
import { useModalManager } from 'components/common/Modal';
import { capitalizeWords } from 'modules/TCIUtils';
import Spinner from '@/components/common/Spinner';
import { UserType } from '../types';
import AssignmentCards from '../Cards/AssignmentCard/AssignmentCards';
import Section from '../Section/index';
import { assignmentsQueryKey, useAssignments } from '../hooks/useAssignments';

interface ToDoCardProps {
  user: UserType;
  classroomId: number;
  coTeacherVisibility: boolean;
  lessonId: number;
  programId: number;
}

const ToDoSection = ({ user, classroomId, coTeacherVisibility, lessonId, programId }: ToDoCardProps) => {
  const isStudent = user.type === 'Student';
  const modalManager = useModalManager();
  const queryClient = useQueryClient();

  const { data: assignments, isLoading, error } = useAssignments({
    classroomId,
    programId,
    user,
  });

  const { upcomingAssignments, overdueAssignments, totalAssignments } = useMemo(() => {
    if (!assignments) return { overdueAssignments: [], totalAssignments: 0, upcomingAssignments: [] };

    const isReadingOnly = (assignment: any) => Object
      .keys(assignment.assignment_models_hash)
      .every(key => key === 'Section');

    const toDo = (assignment: any) => assignment.status === 'to_do';
    const pastDue = (assignment: any) => (assignment.status === 'past_due'
      && !isReadingOnly(assignment) // Remove when reading only assignments can be turned in
      && !assignment.dates_enforced);

    const studentUpcoming = assignments.filter((assignment: any) => toDo(assignment));
    const studentOverdue = assignments.filter((assignment: any) => pastDue(assignment));
    const stafferCurrent = assignments.filter((assignment: any) => new Date(assignment.due_datetime) >= new Date());

    const upcoming = isStudent ? studentUpcoming : stafferCurrent;
    const overdue = isStudent ? studentOverdue : [];
    const total = upcoming.length + overdue.length;

    return {
      overdueAssignments: overdue,
      totalAssignments: total,
      upcomingAssignments: upcoming,
    };
  }, [assignments, isStudent]);

  const createAssignmentButton = () => {
    if (isStudent) {
      return (
        <a
          className="btn btn--med btn--outline-purple tw-font-normal"
          href={Routes.shared_program_assignments_path(programId, { classroom_id: classroomId })}
        >
          {capitalizeWords(I18n.t('go_to_assignments'))}
        </a>
      );
    }

    return (
      <button
        aria-controls="CreateAssignmentModal"
        aria-haspopup="dialog"
        className="btn btn--sm btn--outline-purple"
        type="button"
        onClick={modalManager.open}
      >
        {capitalizeWords(I18n.t('create_assignment'))}
      </button>
    );
  };

  const getSectionProps = (isEmpty: boolean) => ({
    headerColorClass: 'tw-bg-tci-purple',
    headerIcon: <i className="fa fa-icon fa-calendar tw-mr-4" />,
    headerLink: createAssignmentButton(),
    headerTitle: isStudent ? I18n.t('to_do') : I18n.t('current_assignments'),
    isEmpty,
  });

  if (isLoading) {
    return (
      <Section {...getSectionProps(false)}>
        <div className="tw-flex tw-justify-center tw-items-center tw-py-8">
          <Spinner />
        </div>
      </Section>
    );
  }

  if (error) {
    return (
      <Section {...getSectionProps(true)}>
        <div className="tw-text-center tw-py-4 tw-text-red-600">
          {I18n.t('error_loading_assignments')}
        </div>
      </Section>
    );
  }

  return (
    <Section {...getSectionProps(totalAssignments === 0)}>
      {isStudent && overdueAssignments.length > 0 && (
        <AssignmentCards
          assignments={overdueAssignments}
          coTeacherVisibility={coTeacherVisibility}
          subsection="overdue"
          user={user}
          useSubsections={overdueAssignments.length > 0}
        />
      )}
      <AssignmentCards
        assignments={upcomingAssignments}
        coTeacherVisibility={coTeacherVisibility}
        subsection="upcoming"
        user={user}
        useSubsections={isStudent && overdueAssignments.length > 0}
      />

      <AsyncAssignmentModal
        closeModal={modalManager.close}
        handlePostSave={() => queryClient.invalidateQueries(
          assignmentsQueryKey({ classroomId, programId, user }),
        )}
        initialLessonId={lessonId}
        modalIsOpen={modalManager.isOpen}
        programId={programId}
        stafferId={user.id}
      />
    </Section>
  );
};

const ToDoSectionWrapper = ({ classroomId, coTeacherVisibility, lessonId, programId, user }: ToDoCardProps) => (
  <ReactQueryProvider>
    <ToDoSection
      classroomId={classroomId}
      coTeacherVisibility={coTeacherVisibility}
      lessonId={lessonId}
      programId={programId}
      user={user}
    />
  </ReactQueryProvider>
);

export default ToDoSectionWrapper;
